/* eslint-disable */

import { useState, useEffect, useRef } from 'react'
import { format, subMonths } from 'date-fns'
import { CalendarIcon, ChevronUp, ChevronDown, Clock, Download, FileText, RefreshCw, Search, Upload } from 'lucide-react'

import { Button } from '../../Components/ui/button'
import { Calendar } from '../../Components/ui/calendar'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../Components/ui/card'
import { Popover, PopoverContent, PopoverTrigger } from '../../Components/ui/popover'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../Components/ui/table'
import { Badge } from '../../Components/ui/badge'
import { useRecoilValueLoadable } from 'recoil'
import { FetchSIPsOrders } from '../../store/AdminStore'
import { formatIndianCurrency, toCurrency } from '../../utils/CurrencyUtil'
import { Input } from '../../Components/ui/input'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../Components/ui/accordian'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../Components/ui/dialog'
import { Label } from '../../Components/ui/label'
import { post } from '../../http/restservice'
import { CalendarDatePicker } from '../../Components/ui/calendar-date-picker'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../Components/ui/select'

const getStatusBadge = (isChequeOrder, chequeS3Key) => {
  let status
  let colorClass
  let icon

  if (isChequeOrder && chequeS3Key) {
    icon = <FileText className='w-3 h-3 mr-1' />

    status = 'Cheque Uploaded'
    colorClass = 'bg-green-100 text-green-800 hover:bg-green-100'
  } else {
    icon = <Clock className='w-3 h-3 mr-1' />

    status = 'Cheque Pending'
    colorClass = 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100'
  }

  return (
    <Badge className={colorClass}>
      {icon}
      {status}
    </Badge>
  )
}

const GroupedOrders = ({ groupedOrders, sortColumn, sortDirection, onSort }) => {
  console.log('groupedOrders', groupedOrders)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedOrderGroup, setSelectedOrderGroup] = useState(null)

  // States for modal and upload
  const [clientCode, setClientCode] = useState(null)
  const [customerBasketInvestmentId, setCustomerBasketInvestmentId] = useState(null)
  const [currentOrderForUpload, setCurrentOrderForUpload] = useState(null)
  const [selectedFile, setSelectedFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [uploadMessage, setUploadMessage] = useState(null)
  const [uploadError, setUploadError] = useState(null)
  const fileInputRef = useRef(null) // Ref for the file input in the modal

  const handleFileChange = (event) => {
    setUploadMessage(null)
    setUploadError(null)
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0]
      if (file.type === 'application/pdf') {
        setSelectedFile(file)
      } else {
        setSelectedFile(null)
        setUploadError('Please select a PDF file.')
      }
    } else {
      setSelectedFile(null)
    }
  }

  const handleUploadSubmit = async () => {
    if (!selectedFile || !currentOrderForUpload) {
      setUploadError('Please select a file and ensure an order is selected for upload.')
      return
    }

    setUploading(true)
    setUploadMessage(null)
    setUploadError(null)

    const formData = new FormData()
    formData.append('file', selectedFile)
    formData.append('clientCode', clientCode)
    formData.append('orderNos', currentOrderForUpload)
    formData.append('customerBasketInvestmentId', customerBasketInvestmentId)

    try {
      const response = await post(
        'https://apiuat-mywealth.fabits.com/mutualfundservice/api/mutualFund/uploadChallanForChequeOrders',
        formData,
      )

      if (response?.status === 'SUCCESS' && response?.uploadedKey) {
        setUploadMessage(
          `File "${
            selectedFile.name
          }" for Order ${currentOrderForUpload} uploaded successfully! Response: ${JSON.stringify(
            response,
          )}`,
        )

        setSelectedFile(null) // Clear selected file after successful upload
        if (fileInputRef.current) {
          fileInputRef.current.value = '' // Reset file input
        }

        setTimeout(() => {
          setIsModalOpen(false)
          setUploadMessage(null)
        }, 2000)
        // Close modal on success
      }

      if (response?.isError) {
        setSelectedFile(null) // Clear selected file after successful upload
        if (fileInputRef.current) {
          fileInputRef.current.value = '' // Reset file input
        }
        setIsModalOpen(false) // Close modal on success
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
      }
    } catch (err) {
      setUploadError(
        `Failed to upload file for Order ${currentOrderForUpload.orderNumber}: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
      )
    } finally {
      setUploading(false)
    }
  }

  const openUploadModal = (group) => {
    setSelectedOrderGroup(group)
    setIsModalOpen(true)
  }

  const totalAmount = (sips) => {
    return sips?.reduce((acc, sip) => acc + Number(sip?.sipAmount || 0), 0)
  }

  const sipStatusInIllusion = (sips) => {
    return sips?.reduce(
      (acc, sip) => acc + (sip?.registrationStatus === 'Not Registered' ? 1 : 0),
      0,
    )
  }

  const sipStatusRegistered = (sips) => {
    return sips?.reduce((acc, sip) => acc + (sip?.registrationStatus === 'Registered' ? 1 : 0), 0)
  }

  const formatDate = (dateStr) => {
    // handle "YYYY-MM-DD HH:mm:ss"
    if (dateStr.includes('-')) {
      const [datePart] = dateStr.split(' ')
      const [y, m, d] = datePart.split('-')
      return `${d}/${m}/${y}`
    }

    // already in DD/MM/YYYY
    return dateStr
  }

  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return <ChevronUp className='w-3 h-3 opacity-30' />
    }
    return sortDirection === 'asc' ? (
      <ChevronUp className='w-3 h-3' />
    ) : (
      <ChevronDown className='w-3 h-3' />
    )
  }

  const handleSort = (column) => {
    if (onSort) {
      onSort(column)
    }
  }

  return (
    <>
      {/* Header Row - Hidden on mobile */}
      <div className='hidden md:block w-full px-2 py-3 bg-secondary-100 rounded-md mb-2'>
        <div className='flex items-center gap-4'>
          <span className='font-semibold text-secondary-700 text-xs md:text-sm flex-1 min-w-[150px]'>
            Client Code
          </span>

          <span className='font-semibold text-secondary-700 text-xs md:text-sm flex-1 min-w-[150px]'>
            Client Name
          </span>
          <span className='font-semibold text-secondary-700 text-xs md:text-sm flex-1 min-w-[200px]'>
            Basket Name
          </span>
          <span
            className='font-semibold text-secondary-700 text-xs md:text-sm min-w-[120px] cursor-pointer hover:text-secondary-900 flex items-center gap-1 select-none'
            onClick={() => handleSort('totalAmount')}
          >
            Total Amount
            {getSortIcon('totalAmount')}
          </span>
          <span
            className='font-semibold text-secondary-700 text-xs md:text-sm flex-1 min-w-[150px] cursor-pointer hover:text-secondary-900 flex items-center gap-1 select-none'
            onClick={() => handleSort('illusionDate')}
          >
            Illusion Date
            {getSortIcon('illusionDate')}
          </span>

          <span
            className='font-semibold text-secondary-700 text-xs md:text-sm flex-1 min-w-[150px] cursor-pointer hover:text-secondary-900 flex items-center gap-1 select-none'
            onClick={() => handleSort('sipRegisteredDate')}
          >
            SIP Reg. Date
            {getSortIcon('sipRegisteredDate')}
          </span>

          <span
            className='font-semibold text-secondary-700 text-xs md:text-sm flex-1 min-w-[150px] cursor-pointer hover:text-secondary-900 flex items-center gap-1 select-none'
            onClick={() => handleSort('illusionSipDate')}
          >
            SIP Start Date
            {getSortIcon('illusionSipDate')}
          </span>

          <span className='font-semibold text-secondary-700 text-xs md:text-sm flex-1 min-w-[150px]'>
            Status
          </span>
        </div>
      </div>

      <Accordion type='multiple' className='w-full space-y-2'>
        {groupedOrders.map((group, groupIndex) => (
          <AccordionItem key={groupIndex} value={`group-${groupIndex}`} className='border-none'>
            <AccordionTrigger
              className={`${
                sipStatusInIllusion(group) > 0 ? 'bg-warning-50' : 'bg-secondary-50'
              } px-2 md:px-4 py-3 rounded-md border-none cursor-pointer`}
            >
              <div className='flex flex-col text-left space-y-2 w-full'>
                <div className='flex flex-col md:flex-row md:items-center gap-2 md:gap-4'>
                  <span className='font-semibold text-sm md:text-base flex-1 md:min-w-[150px]'>
                    {group[0].clientCode}
                  </span>

                  <span className='font-semibold text-sm md:text-base flex-1 md:min-w-[150px]'>
                    {group[0].clientName}
                  </span>
                  <span className='font-medium text-xs md:text-sm flex-1 md:min-w-[200px]'>
                    {group[0].customerBasketName} ({group.length} SIP{group.length > 1 ? 's' : ''})
                  </span>

                  <span className='text-xs md:text-sm text-muted-foreground md:min-w-[120px] text-left md:text-right font-semibold'>
                    ₹ {formatIndianCurrency(toCurrency(totalAmount(group)))}
                  </span>

                  <span className='font-semibold text-sm md:text-base flex-1 md:min-w-[150px]'>
                    {formatDate(group[0].illusionDate)}
                  </span>
                  
                  <span className='font-semibold text-sm md:text-base flex-1 md:min-w-[150px]'>
                    {formatDate(group[0].sipRegisteredDate)}
                  </span>

                  <span className='font-semibold text-sm md:text-base flex-1 md:min-w-[150px]'>
                    {formatDate(group[0].illusionSipDate)}
                  </span>

                  <span
                    className={`text-xs md:text-sm flex-1 md:min-w-[150px] ${
                      sipStatusInIllusion(group) > 0 ? 'text-warning-500' : 'text-secondary-500'
                    }`}
                  >
                    {sipStatusInIllusion(group) > 0
                      ? ` ${sipStatusInIllusion(group)} In Illusion`
                      : `All Registered`}
                  </span>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className='space-y-3 pt-2'>
              <div className='overflow-x-auto -mx-2 md:mx-0'>
                <Table className='min-w-[600px]'>
                  <TableHeader>
                    <TableRow>
                      {/* <TableHead>Illusion ID</TableHead> */}
                      <TableHead className='text-xs md:text-sm'>Basket ID</TableHead>
                      <TableHead className='text-xs md:text-sm'>Basket Name</TableHead>
                      <TableHead className='text-xs md:text-sm'>Registration Status</TableHead>
                      <TableHead className='text-xs md:text-sm'>Scheme Code</TableHead>
                      <TableHead className='text-xs md:text-sm'>SIP Status</TableHead>
                      <TableHead className='text-right text-xs md:text-sm'>Amount</TableHead>
                      <TableHead className='text-xs md:text-sm'>Mandate ID</TableHead>
                      {/* <TableHead>Client Code</TableHead>
                    <TableHead>Created Date</TableHead>
                    <TableHead>Status</TableHead> */}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {group.map((sip) => (
                      <TableRow key={sip.id}>
                        {/* <TableCell className='font-medium'>{sip.id}</TableCell> */}
                        <TableCell className='font-semibold text-xs md:text-sm'>
                          {sip.customerBasketInvestmentId}
                        </TableCell>
                        <TableCell className='text-xs md:text-sm'>
                          {sip.customerBasketName}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant='outline'
                            className={`text-xs ${
                              sip.registrationStatus === 'Not Registered'
                                ? 'bg-error-500 text-white'
                                : 'bg-green-500 text-white'
                            }`}
                          >
                            {sip.registrationStatus}
                            {/* {order.buySell} */}
                          </Badge>
                        </TableCell>
                        <TableCell className='text-xs md:text-sm'>{sip.schemeCode}</TableCell>
                        <TableCell className='text-xs md:text-sm'>{sip.sipStatus}</TableCell>
                        <TableCell className='text-right font-medium text-xs md:text-sm'>
                          ₹ {formatIndianCurrency(toCurrency(sip?.sipAmount || 0))}
                        </TableCell>
                        <TableCell className='text-xs md:text-sm'>{sip.mandateId}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </>
  )
}

const SIPsOrders = () => {
  const [selectedDateRange, setSelectedDateRange] = useState(() => {
    const today = new Date()
    const sevenDaysAgo = new Date()
    sevenDaysAgo.setDate(today.getDate() - 7)

    return {
      from: sevenDaysAgo,
      to: today,
    }
  })

  const [searchQuery, setSearchQuery] = useState('')
  const [sortColumn, setSortColumn] = useState(null)
  const [sortDirection, setSortDirection] = useState('asc')
  const [statusFilter, setStatusFilter] = useState('all')
  
  const fromDateStr = format(selectedDateRange.from, 'yyyy-MM-dd')
  const toDateStr = format(selectedDateRange.to, 'yyyy-MM-dd')
  const { state, contents: orders } = useRecoilValueLoadable(
    FetchSIPsOrders({ fromDateStr, toDateStr }),
  )

  const filteredOrders =
    searchQuery && state === 'hasValue'
      ? orders?.data?.filter((order) =>
          order.clientName?.toLowerCase()?.includes(searchQuery?.toLowerCase()),
        )
      : orders?.data

  const exportToCSV = () => {
    const headers = [
      'Order Number',
      'Phone Number',
      'Scheme Code',
      'Buy/Sell Type',
      'Amount',
      'Client Code',
      'Created Date',
      'Status',
    ]
    const csvContent = [
      headers.join(','),
      ...orders?.data?.map((order) =>
        [
          order.orderNumber,
          order.phoneNumber,
          order.schemeCode,
          `${order.buySellType} ${order.buySell}`,
          order.amount,
          order.clientCode,
          order.createdTimeStamp,
          order.successFlag === '1' ? 'Success' : order.chequeS3Key ? 'Cheque Uploaded' : 'Pending',
        ].join(','),
      ),
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `cheque-orders-${format(new Date(), 'yyyy-MM-dd')}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  function parseDate(ts) {
    return new Date(ts?.replace(' ', 'T'))
  }

  function parseDateForSort(dateStr) {
    if (!dateStr) return new Date(0)
    // Handle "YYYY-MM-DD HH:mm:ss" format
    if (dateStr.includes('-')) {
      const [datePart] = dateStr.split(' ')
      return new Date(datePart)
    }
    // Handle "DD/MM/YYYY" format
    if (dateStr.includes('/')) {
      const [d, m, y] = dateStr.split('/')
      return new Date(`${y}-${m}-${d}`)
    }
    return new Date(dateStr)
  }

  function calculateTotalAmount(group) {
    return group?.reduce((acc, sip) => acc + Number(sip?.sipAmount || 0), 0)
  }

  function hasItemsInIllusion(group) {
    return group?.some((sip) => sip?.registrationStatus === 'Not Registered')
  }

  function filterGroupedOrders(groups, filter) {
    if (filter === 'all') {
      return groups
    }
    if (filter === 'registered') {
      return groups.filter((group) => !hasItemsInIllusion(group))
    }
    if (filter === 'illusion') {
      return groups.filter((group) => hasItemsInIllusion(group))
    }
    return groups
  }

  function groupOrders(orders) {
    const groups = []

    const sortedOrders = [...orders]?.sort(
      (a, b) => parseDate(a.createdTimeStamp) - parseDate(b.createdTimeStamp),
    )

    for (let order of orders) {
      const foundGroup = groups.find((group) => {
        return group[0].id === order.id
      })

      if (foundGroup) {
        foundGroup.push(order)
      } else {
        groups.push([order])
      }
    }

    return groups
  }

  function sortGroupedOrders(groups, column, direction) {
    if (!column) return groups

    const sorted = [...groups].sort((a, b) => {
      let comparison = 0

      switch (column) {
        case 'totalAmount':
          comparison = calculateTotalAmount(a) - calculateTotalAmount(b)
          break
        case 'illusionDate':
          comparison =
            parseDateForSort(a[0]?.illusionDate) - parseDateForSort(b[0]?.illusionDate)
          break
        case 'sipRegisteredDate':
          comparison =
            parseDateForSort(a[0]?.sipRegisteredDate) - parseDateForSort(b[0]?.sipRegisteredDate)
          break
        case 'illusionSipDate':
          comparison =
            parseDateForSort(a[0]?.illusionSipDate) - parseDateForSort(b[0]?.illusionSipDate)
          break
        default:
          return 0
      }

      return direction === 'asc' ? comparison : -comparison
    })

    return sorted
  }

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      // New column, default to ascending
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  return (
    <div className='mx-auto p-2 md:p-6 space-y-4 md:space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-xl md:text-2xl lg:text-3xl font-bold tracking-tight'>SIPs Orders</h1>
          <p className='text-muted-foreground text-xs md:text-sm'>
            Manage and track all SIPs orders for mutual fund investments
          </p>
        </div>
      </div>

      <Card>
        <CardHeader className='px-3 md:px-6'>
          <CardTitle className='text-base md:text-lg'>Filter SIPs Orders</CardTitle>
          <CardDescription className='text-xs md:text-sm'>
            Select date range to fetch SIPs orders. Default shows last 1 month data.
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='flex flex-col sm:flex-row gap-4 items-end'>
            <div className='grid gap-2'>
              <label className='text-sm font-medium'>Select Date Range</label>
              <CalendarDatePicker date={selectedDateRange} onDateSelect={setSelectedDateRange} />

              {/* <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant='outline'
                    className={cn(
                      'w-[240px] justify-start text-left font-normal',
                      !fromDate && 'text-muted-foreground',
                    )}
                  >
                    <CalendarIcon className='mr-2 h-4 w-4' />
                    {fromDate ? format(fromDate, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className='w-auto p-0' align='start'>
                  <Calendar
                    mode='single'
                    selected={fromDate}
                    onSelect={setFromDate}
                    initialFocus
                    showOutsideDays={true}
                    captionLayout='dropdown-buttons'
                    fromYear={2020}
                    toYear={2030}
                  />
                </PopoverContent>
              </Popover> */}
            </div>

            {/* <div className='grid gap-2'>
              <label className='text-sm font-medium'>To Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant='outline'
                    className={cn(
                      'w-[240px] justify-start text-left font-normal',
                      !toDate && 'text-muted-foreground',
                    )}
                  >
                    <CalendarIcon className='mr-2 h-4 w-4' />
                    {toDate ? format(toDate, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className='w-auto p-0' align='start'>
                  <Calendar
                    mode='single'
                    selected={toDate}
                    onSelect={setToDate}
                    initialFocus
                    showOutsideDays={true}
                    captionLayout='dropdown-buttons'
                    fromYear={2020}
                    toYear={2030}
                  />
                </PopoverContent>
              </Popover>
            </div> */}

            {/* <div className='flex gap-2'>
              <Button
                // onClick={fetchSIPsOrders}
                disabled={!selectedDateRange.from || !selectedDateRange.to}
                className='gap-2'
              >
                {state === 'loading' ? (
                  <RefreshCw className='h-4 w-4 animate-spin' />
                ) : (
                  <Search className='h-4 w-4' />
                )}
                {state === 'loading' ? 'Fetching...' : 'Fetch Orders'}
              </Button>

              <Button
                variant='outline'
                onClick={exportToCSV}
                disabled={orders.length === 0}
                className='gap-2'
              >
                <Download className='h-4 w-4' />
                Export CSV
              </Button>
            </div> */}
          </div>

          <div className='flex flex-col sm:flex-row gap-4 items-end'>
            {/* New Client Code Filter */}
            {orders?.data?.length && (
              <div className='grid gap-2'>
                <label htmlFor='clientCode' className='text-sm font-medium'>
                  Client Name{' '}
                </label>
                <Input
                  id='clientName'
                  placeholder='Enter client name'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className='w-[280px]'
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {state === 'hasError' && (
        <Card className='border-destructive'>
          <CardContent className='pt-6'>
            <div className='flex items-center gap-2 text-destructive'>
              <span className='font-medium'>Error:</span>
              <span>Something went wrong, please try again later</span>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className='px-3 md:px-6'>
          <div className='flex items-center justify-between gap-4'>
            <div className='flex-1'>
              <CardTitle className='text-base md:text-lg'>
                SIPs (
                {filteredOrders?.length > 0
                  ? filterGroupedOrders(groupOrders(filteredOrders), statusFilter)?.length || 0
                  : 0}
                )
              </CardTitle>
              <CardDescription className='text-xs md:text-sm'>
                {selectedDateRange.from && selectedDateRange.to && (
                  <>
                    Showing orders from {format(selectedDateRange.from, 'PPP')} to{' '}
                    {format(selectedDateRange.to, 'PPP')}
                  </>
                )}
              </CardDescription>
            </div>
            <div className='flex items-center gap-2'>
              <Select value={statusFilter} onValueChange={setStatusFilter} >
                <SelectTrigger className='w-[180px] border'>
                  <SelectValue placeholder='Filter by status' />
                </SelectTrigger>
                <SelectContent >
                  <SelectItem value='all'>All</SelectItem>
                  <SelectItem value='registered'>All Registered</SelectItem>
                  <SelectItem value='illusion'>In Illusion</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>

        <CardContent className='px-3 md:px-6'>
          {state === 'loading' ? (
            <div className='flex items-center justify-center py-8'>
              <RefreshCw className='h-8 w-8 animate-spin text-muted-foreground' />
              <span className='ml-2 text-muted-foreground'>Loading SIPs orders...</span>
            </div>
          ) : state === 'hasValue' &&
            orders?.message === 'No SIPs orders found for the specified user.' ? (
            <div className='text-center py-8 text-muted-foreground'>{orders?.message} </div>
          ) : (
            <div>
              {filteredOrders?.length > 0 ? (
                <GroupedOrders
                  groupedOrders={sortGroupedOrders(
                    filterGroupedOrders(groupOrders(filteredOrders), statusFilter),
                    sortColumn,
                    sortDirection,
                  )}
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
              ) : (
                <div className='text-center py-8 text-muted-foreground'>No SIPs orders found</div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default SIPsOrders
